import * as moment from 'moment';
import { IWatrRequestTravels, IWatrRequestWeeks, IWatrReimbursement } from '@nkr/models/index';

export type ApproverDisplay = {
    name: string;
    dateText: string;
};

export type ReimbursedDisplay = {
    name: string;
    dateText: string;
};

type AuditStampSource = {
    userId?: number | null;
    userName?: string | null;
    dateTime?: string | Date | null;
};

export const formatApproverDateTime = (value?: string | Date | null, compact?: boolean): string => {
    if (!value) return '';
    const m = moment(value);
    if (!m.isValid()) return '';
    return compact ? m.format('MM/DD/YY h:mm A') : m.format('MM/DD/YYYY h:mm A');
};

export const formatDateOnly = (value?: string | Date | null, compact?: boolean): string => {
    if (!value) return '';
    const m = moment(value);
    if (!m.isValid()) return '';
    return compact ? m.format('MM/DD/YY') : m.format('MM/DD/YYYY');
};

const resolveAuditDisplay = (sources: AuditStampSource[], compact?: boolean): ApproverDisplay => {
    const withStamp = (sources || []).filter(s =>
        (s.userId ?? 0) > 0 || (s.userName || '').trim() || s.dateTime
    );
    if (withStamp.length === 0) {
        return { name: '', dateText: '' };
    }

    const userIds = Array.from(new Set(
        withStamp
            .map(s => s.userId ?? 0)
            .filter(id => id > 0)
    ));

    const names = Array.from(new Set(
        withStamp
            .map(s => (s.userName || '').trim())
            .filter(Boolean)
    ));

    const isMultiple = userIds.length > 1 || (userIds.length === 0 && names.length > 1);
    if (isMultiple) {
        return { name: 'Multiple', dateText: '' };
    }

    const name = names[0] || '';
    const earliest = withStamp
        .map(s => s.dateTime)
        .filter(Boolean)
        .map(v => moment(v))
        .filter(m => m.isValid())
        .sort((a, b) => a.valueOf() - b.valueOf())[0];

    return {
        name,
        dateText: earliest ? earliest.format(compact ? 'MM/DD/YY h:mm A' : 'MM/DD/YYYY h:mm A') : ''
    };
};

export const resolveApproverDisplayForWeeks = (weeks: IWatrRequestWeeks[], compact?: boolean): ApproverDisplay => {
    return resolveAuditDisplay(
        (weeks || []).map(w => ({
            userId: w.approverUserId,
            userName: w.approverName,
            dateTime: w.approvedDateTime
        })),
        compact
    );
};

export const resolveApproverDisplayForTravels = (travels: IWatrRequestTravels[], compact?: boolean): ApproverDisplay => {
    return resolveAuditDisplay(
        (travels || []).map(t => ({
            userId: t.approverUserId,
            userName: t.approverName,
            dateTime: t.approvedDateTime
        })),
        compact
    );
};

export const resolveApproverDisplayForReimbursementLog = (
    weeks: IWatrRequestWeeks[],
    travels: IWatrRequestTravels[],
    compact?: boolean
): ApproverDisplay => {
    const fromWeeks = resolveApproverDisplayForWeeks(weeks, compact);
    if (fromWeeks.name || fromWeeks.dateText) {
        return fromWeeks;
    }
    return resolveApproverDisplayForTravels(travels, compact);
};

export const resolveReimbursedDisplayForReimbursementLog = (
    item: IWatrReimbursement,
    compact?: boolean
): ReimbursedDisplay => {
    const fromReimbursement = resolveAuditDisplay(
        [{
            userId: item.reimbursedUserId,
            userName: item.reimbursedUserName,
            dateTime: item.reimbursedDateTime
        }],
        compact
    );

    if (fromReimbursement.dateText) {
        return fromReimbursement;
    }

    const dateOnly = formatDateOnly(item.createdDateTime || item.date, compact);
    if (!dateOnly) {
        return fromReimbursement;
    }

    return {
        name: fromReimbursement.name,
        dateText: dateOnly
    };
};

export const parseWeekNumbers = (weeksRequested?: string | null): number[] => {
    if (!weeksRequested || weeksRequested.trim() === '') return [];
    return weeksRequested
        .split(/[,\s]+/)
        .map(s => parseInt(s.trim(), 10))
        .filter(n => !isNaN(n) && n > 0);
};

export const findWeeksForReimbursement = (
    requestWeeks: IWatrRequestWeeks[] | null | undefined,
    reimbursementId: number,
    weeksRequested?: string | null
): IWatrRequestWeeks[] => {
    const weeks = requestWeeks || [];
    const byReimbursementId = weeks.filter(w => w.reimbursementId === reimbursementId);
    if (byReimbursementId.length > 0) {
        return byReimbursementId;
    }

    const weekNumbers = parseWeekNumbers(weeksRequested);
    if (weekNumbers.length === 0) {
        return [];
    }

    return weeks.filter(w => weekNumbers.indexOf(w.weekRequested) >= 0);
};

export const findTravelsForReimbursement = (
    requestTravels: IWatrRequestTravels[] | null | undefined,
    reimbursementId: number
): IWatrRequestTravels[] => {
    return (requestTravels || []).filter(t => t.reimbursementId === reimbursementId);
};
